import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Header';
import { currentDrawerId, getDailyReports } from 'api';
import type { Report } from 'api/types';
import { useSessionCheckTotal } from 'hooks/useSessionCheckTotal'; // NEW

// Never render these keys:
const HIDDEN_KEYS = ['net_cash_in', 'net_cash_out'];

// Custom labels for the rest:
const LABEL_OVERRIDES: Record<string, string> = {
  open_float: 'Opening Cash On Hand',
  net_sales: 'Cash Sales',
  net_non_sale_cash_in: 'Non-Sale Cash in',
  net_non_sale_cash_out: 'Non-Sale Cash out',
  net_returns: 'Cash Returns',
};

function formatDateString(isoString: string): string {
  return new Date(isoString).toLocaleString([], {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const formatCurrency = (amount: { value: number; currency: string }) =>
  amount.value.toLocaleString('en-US', {
    style: 'currency',
    currency: amount.currency || 'USD',
  });

// Turn snake_case key into Title Case label
function humanize(key: string) {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const SummaryPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    isLoading: checksLoading,
    errorMessage: checksError,
    checkTotal,
  } = useSessionCheckTotal({
    activation: report?.activation_timestamp,
    deactivation: report?.deactivation_timestamp ?? null,
    storeId: currentDrawerId(),
  }, { enabled: !!report, autoDetect: false });
  const isLoading = loading || checksLoading;
  const compositeError = error || checksError || null;

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const { reports } = await getDailyReports();
        if (!reports.length) throw new Error('No reports found');

        // Always pick the very latest report
        const latest = reports[0];
        setReport(latest);
      } catch (err) {
        console.error(err);
        setError('Failed to load summary or check payments.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);


  const rows = useMemo(() => {
    if (!report) return [];
    const dynamic = Object.entries(report)
      .filter(([key]) => !HIDDEN_KEYS.includes(key))
      .map(([key, val]) => {
        let valueStr: string;

        if (key.endsWith('_timestamp') && typeof val === 'string') {
          valueStr = formatDateString(val);
        } else if (typeof val === 'object' && val !== null && 'value' in val && 'currency' in val) {
          valueStr = formatCurrency(val as { value: number; currency: string });
        } else {
          valueStr = String(val);
        }
        const label = LABEL_OVERRIDES[key] ?? humanize(key);
        return { label, value: valueStr, rawKey: key };
      });

    // Always add Check Payments last
    dynamic.push({
      label: 'Check Payments',
      value: formatCurrency({ value: checkTotal, currency: 'USD' }),
      rawKey: '__checks__',
    });

    return dynamic;
  }, [report, checkTotal]);

  // Pull timestamps for context bar if available
  const activationLabel =
    report?.activation_timestamp ? formatDateString(report.activation_timestamp) : null;
  const deactivationLabel = report?.deactivation_timestamp
    ? formatDateString(report.deactivation_timestamp)
    : 'Active';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Summary</Typography>}
        subtitle="End-of-day overview"
        showBackButton
        onBack={() => navigate(-1)}
        sticky
        align="left"
        elevateOnScroll
      />

      <Container
        disableGutters
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 720, md: 880 },
          mx: 'auto',
        }}
      >
        <Box
          component="main"
          sx={{
            mx: 2,
            mt: 2,
            mb: 6,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            background: alpha(theme.palette.primary.light, 0.05),
            backdropFilter: 'saturate(120%) blur(4px)',
          }}
        >

          {/* Context Card */}
          <Card
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" sx={{
                gap: 1, // applies both row and column spacing
              }}
                flexWrap="wrap" alignItems="center">
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Activation: ${activationLabel ?? '—'}`}
                  sx={{ borderRadius: 2, margin: { xs: 1 } }}
                />
                <Chip
                  size="small"
                  color={report?.deactivation_timestamp ? 'default' : 'success'}
                  variant={report?.deactivation_timestamp ? 'outlined' : 'filled'}
                  label={`Deactivation: ${deactivationLabel ?? '—'}`}
                  sx={{ borderRadius: 2, margin: { xs: 1 } }}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Main panel */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              background: alpha(theme.palette.primary.light, 0.05),
              backdropFilter: 'saturate(120%) blur(4px)',
              minHeight: 160,
            }}
          >
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : compositeError ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="error">{error}</Typography>
              </Box>
            ) : (
              <Box>
                {rows.map((row, idx) => {
                  const isLast = idx === rows.length - 1;
                  const isNegative = /^-/.test(row.value) || row.value.includes('−');
                  const isTimestamp = /timestamp/i.test(row.rawKey ?? '');

                  return (
                    <Box key={row.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: 0.5,
                          py: 1.25,
                          px: { xs: 1, sm: 1.5 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: 14, sm: 15 },
                            color: 'text.secondary',
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: isTimestamp
                              ? 'text.primary'
                              : isNegative
                                ? theme.palette.error.main
                                : 'text.primary',
                            fontWeight: isTimestamp ? 500 : 700,
                          }}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                      {!isLast && <Divider sx={{ mx: { xs: 1, sm: 1.5 } }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

    </Box>
  );
};

export default SummaryPage;
